import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button, Checkbox, Container } from '@dansk-metal/ui';

import { FormFooter, FormHeader, FormWrapper } from '@web/components/form-wrapper/form-wrapper';
import { ThemedSkeleton } from '@web/components/themed-skeleton/themed-skeleton';
import { UmbracoLink } from '@web/components/umbraco-link/umbraco-link';

import { useAuthenticatedCRMRequestConfig } from '@web/services/crm-api/hooks';
import { Channel, Consent, useGetActiveConsentsByMember, useGetMemberById, useRegisterConsents } from '@web/services/crm-api/rest';
import { Link } from '@web/services/umbraco/types/basic/Link';

import styles from './consents-form.module.scss';

export type ConsentsFormProps = {
	contactLink: Link;
	privatePolicyLink: Link;
};

export function ConsentsForm({ contactLink, privatePolicyLink }: ConsentsFormProps) {
	const { memberId, requestConfig } = useAuthenticatedCRMRequestConfig();

	const {
		data: member,
		isLoading: isMemberLoading,
	} = useGetMemberById(memberId, { request: requestConfig });

	const { data: memberConsent, isLoading: isMemberConsentLoading } = useGetActiveConsentsByMember(
		memberId,
		{ request: requestConfig },
	);

	const {
		mutateAsync,
		isError,
		isSuccess,
		isLoading: isConsentsLoading,
	} = useRegisterConsents({ request: requestConfig });

	const categorizedConsents = (memberConsent || []).reduce(
		(acc, consent) => {
			if (!consent.channel) {
				return acc;
			}

			if (!acc[consent.channel]) {
				acc[consent.channel] = [];
			}

			acc[consent.channel].push(consent);

			return acc;
		},
		{} as {
			[key in Channel]: Consent[];
		},
	);

	const { control, setValue, handleSubmit, setError } = useForm({
		defaultValues: {
			[Channel.Email]: (categorizedConsents[Channel.Email] || []).reduce((acc, consent) => {
				if (consent.id) {
					acc[consent.id] = consent.consented || false;
				}

				return acc;
			}, {} as { [key: string]: boolean }),
			[Channel.Sms]: (categorizedConsents[Channel.Sms] || []).reduce((acc, consent) => {
				if (consent.id) {
					acc[consent.id] = consent.consented || false;
				}

				return acc;
			}, {} as { [key: string]: boolean }),
		},
	});

	useEffect(() => {
		if (memberConsent?.length !== 0) {
			memberConsent?.forEach((consent) => {
				setValue(`${consent.channel as Channel}.${consent.id}`, consent.consented || false);
			});
		}
	}, [memberConsent, memberConsent?.length, setValue]);

	async function onSubmit(data: {
		[key in Channel]: { [key: string]: boolean };
	}) {
		try {
			if (!data) {
				throw new Error('Consents data not ready');
			}

			await mutateAsync({
				memberId,
				data: {
					consents: [
						...Object.keys(data.Email).map((key) => {
							const each = memberConsent?.find((consent) => consent.id === key) as Consent;

							return ({
								...each,
								consented: data.Email[key],
							});
						}),
						...Object.keys(data.Sms).map((key) => {
							const each = memberConsent?.find((consent) => consent.id === key) as Consent;

							return ({
								...each,
								consented: data.Sms[key],
							});
						}),
					],
				},
			});
		} catch (error) {
			setError('root', {
				message: 'Der skete en fejl. Prøv igen senere.',
			});
		}
	}

	const pageLoading = isMemberLoading || isMemberConsentLoading;

	return (
		<Container className={styles.container}>
			{pageLoading ?
				(
					<ThemedSkeleton
						className={styles.skeleton_theme}
					/>
				) : (
					<form onSubmit={handleSubmit(onSubmit)}>
						<FormWrapper>
							<div className={styles.banner_wrapper}>
								{(isError) && <FormHeader type='error' message={'Der er fejl i et eller flere felter'} />}
								{(isSuccess) && <FormHeader type='success' message='Dine oplysninger er blevet gemt' />}
							</div>
							<div className={styles.forms_container}>
								<div className={styles.checkbox_section}>
									<h4 className={styles.checkbox_section__header}>Email</h4>
									<p className={styles.checkbox_section__subheader}>
										{member?.email ? `Din email: ${member?.email} ` : 'Du skal tilføje en emailadresse, før du modtage emails fra os '}
										(<UmbracoLink className={styles.link} link={contactLink}>skift</UmbracoLink>)
									</p>
									<div className={styles.checkbox_list}>
										{(categorizedConsents[Channel.Email] || []).map((consent) => (
											<Controller
												key={consent.id}
												control={control}
												name={`${Channel.Email}.${consent.id}`}
												render={({ field }) => (
													<div className={styles.checkbox_container}>
														<Checkbox
															disabled={isConsentsLoading}
															className={styles.checkbox}
															{...field}
															checked={field.value}
															label={
																<p className={styles.label}>
																	{consent.label}
																</p>
															}
															required
														/>
													</div>
												)}
											/>
										))}
									</div>
								</div>
								<div className={styles.checkbox_section}>
									<h4 className={styles.checkbox_section__header}>SMS</h4>
									<p className={styles.checkbox_section__subheader}>
										{member?.phoneNumber ? `Dit mobilnummer: ${member?.phoneNumber} ` : 'Du skal tilføje et mobilnummer, før du modtage SMS fra os '}
										(<UmbracoLink className={styles.link} link={contactLink}>skift</UmbracoLink>)
									</p>
									<div className={styles.checkbox_list}>
										{(categorizedConsents[Channel.Sms] || []).map((consent) => (
											<Controller
												key={consent.id}
												control={control}
												name={`${Channel.Sms}.${consent.id}`}
												render={({ field }) => (
													<div className={styles.checkbox_container}>
														<Checkbox
															disabled={isConsentsLoading}
															className={styles.checkbox}
															{...field}
															checked={field.value}
															label={
																<p className={styles.label}>
																	{consent.label}
																</p>
															}
															required
														/>
													</div>
												)}
											/>
										))}
									</div>
								</div>
								<div>
									<p>
										Når du tilmelder dig, accepterer du samtidig Dansk
										Metals <UmbracoLink className={styles.link} link={privatePolicyLink}>
											privatlivspolitik</UmbracoLink>.
									</p>
								</div>
							</div>
						</FormWrapper>
						<FormFooter>
							<Button icon={{ icon: isConsentsLoading ? 'spinner' : 'arrow-right', size: 24 }} type="submit" disabled={isConsentsLoading}>
								Gem dine valg
							</Button>
						</FormFooter>
					</form>
				)}
		</Container>
	);
}
